"use client";

import { useRef } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { IoCloseSharp } from "react-icons/io5";
import { useMixPanel } from "@/app/context/mixPanelContext";
import { useClientConfigProvider } from "@/app/config/client-config-provider";
import IntroPointerArrow from "./IntroPointerArrow";

const AINA_INTRO_MODAL_CONFIG = {
  // Arrow tail colour is the client's brand colour, pulled from the global
  // design-token CSS variable so it themes per project automatically.
  arrowGradient: { from: "var(--surface-default-primary)", to: "#FFFFFF" },

  title: "Meet AINA!",
  subtitle: "Your personal AI concierge",

  sections: [
    {
      id: "what-is-aina",
      title: "What is AINA",
      type: "text",
      content:
        "AINA is an AI-powered benefits concierge that helps customers discover, redeem, and manage their card benefits effortlessly.",
      image: "/common-images/aina_what_is.png",
    },
    {
      id: "how-to-book",
      title: "How to book with AINA",
      type: "list",
      content: [
        "Tell AINA what you want",
        "AINA handles the checks",
        "You confirm, AINA books",
      ],
      image: "/common-images/aina_how_to_book.png",
    },
    {
      id: "what-can-i-do",
      title: "What I can do with AINA",
      type: "list",
      content: [
        "Redeem your benefits",
        "Manage benefits",
        "Know more about your benefits and quota",
      ],
      image: "/common-images/aina_what_can_i_do.png",
    },
  ],

  primaryCta: {
    label: "Start booking with AINA",
    event: "chatbot_intro_get_started",
  },

  secondaryCta: {
    label: "I'll browse manually instead",
    event: "chatbot_intro_dismissed",
  },
};

type AinaIntroModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onPrimaryAction: () => void;
};

const renderSectionContent = (section: any) => {
  if (section.type === "list") {
    return (
      <ol>
        {section.content.map((item: string, idx: number) => (
          <li key={idx}>{item}</li>
        ))}
      </ol>
    );
  }

  return <p>{section.content}</p>;
};

export default function AinaIntroModal({
  isOpen,
  onClose,
  onPrimaryAction,
}: AinaIntroModalProps) {
  const { trackEvent } = useMixPanel();
  const { getImagePath } = useClientConfigProvider();
  const cardRef = useRef<HTMLDivElement>(null);

  if (!isOpen) return null;

  return createPortal(
    <div className="aina-intro-modal">
      <div className="aina-intro-modal__backdrop" />

      {/* Onboarding pointer toward the floating AINA button. Rendered before
          the card so the modal overlaps any part that extends under it. */}
      <IntroPointerArrow
        fromRef={cardRef}
        from={AINA_INTRO_MODAL_CONFIG.arrowGradient.from}
        to={AINA_INTRO_MODAL_CONFIG.arrowGradient.to}
      />

      <div className="aina-intro-modal__card" ref={cardRef}>
        {/* Branded header */}
        <div className="aina-intro-modal__header">
          <div className="aina-intro-modal__topbar">
            <Image
              className="aina-intro-modal__bank-logo"
              src={getImagePath("logo-white.png")}
              alt="Logo"
              width={150}
              height={40}
            />
            <button
              className="aina-intro-modal__close"
              onClick={onClose}
              aria-label="Close"
            >
              <IoCloseSharp />
            </button>
          </div>

          <div className="aina-intro-modal__intro">
            <Image
              className="aina-intro-modal__intro-logo"
              src={getImagePath("chatbot_logo.png")}
              alt="AINA"
              width={64}
              height={64}
            />
            <div className="aina-intro-modal__intro-text">
              <h2 className="aina-intro-modal__title">
                {AINA_INTRO_MODAL_CONFIG.title}
              </h2>
              <div className="aina-intro-modal__subtitle">
                {AINA_INTRO_MODAL_CONFIG.subtitle}
              </div>
            </div>
          </div>
        </div>

        <div className="aina-intro-modal__body">
          <div className="aina-intro-modal__sections">
            {AINA_INTRO_MODAL_CONFIG.sections.map((section) => (
              <div key={section.id} className="aina-intro-modal__section">
                {section.image && (
                  <Image
                    src={section.image}
                    alt={section.title}
                    width={56}
                    height={56}
                  />
                )}
                <div className="aina-intro-modal__section__content">
                  <h4>{section.title}</h4>
                  {renderSectionContent(section)}
                </div>
              </div>
            ))}
          </div>

          <div className="aina-intro-modal__ctas">
            <button
              className="aina-intro-modal__primary"
              onClick={() => {
                trackEvent?.(AINA_INTRO_MODAL_CONFIG.primaryCta.event);
                onPrimaryAction();
              }}
            >
              {AINA_INTRO_MODAL_CONFIG.primaryCta.label}
            </button>

            <button
              className="aina-intro-modal__secondary"
              onClick={() => {
                trackEvent?.(AINA_INTRO_MODAL_CONFIG.secondaryCta.event);
                onClose();
              }}
            >
              {AINA_INTRO_MODAL_CONFIG.secondaryCta.label}
            </button>
          </div>

          <div className="aina-intro-modal__footer">
            <span className="aina-intro-modal__powered">Powered by</span>
            <Image
              className="aina-intro-modal__thriwe-logo"
              src={getImagePath("thriwe_logo.png")}
              alt="Thriwe"
              width={70}
              height={18}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
